import React from 'react'

/**
 * Button component with consistent styling
 * @param {Object} props - Component props
 * @param {string} props.variant - Button variant (primary, secondary, danger, success)
 * @param {function} props.onClick - Click handler function
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.fullWidth - Whether button should take full width
 * @param {string} props.size - Button size (small, medium, large)
 * @param {Object} props.rest - Additional props to pass to the button element
 */
const Button = ({
  variant = 'primary',
  onClick,
  children,
  className = '',
  fullWidth = false,
  size = 'medium',
  ...rest
}) => {
  // Base styles
  const baseStyles =
    'font-medium rounded-md transition-all duration-200 focus:outline-none'

  // Size styles - increased touch targets for mobile
  const sizeStyles = {
    small: 'px-3 py-1.5 text-sm',
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-sm',
    medium: 'px-4 py-2.5',
    large: 'px-6 py-3.5 text-lg',
  }

  // Detect if device supports hover
  const hasHover =
    typeof window !== 'undefined' && window.matchMedia('(hover: hover)').matches

  // Base variant styles (without hover)
  const baseVariantStyles = {
    primary: 'bg-green-500 text-white active:bg-green-600',
    secondary: 'bg-gray-200 text-gray-800 active:bg-gray-300',
    tertiary: 'bg-gray-100 text-gray-500 active:bg-gray-200',
    danger: 'bg-red-500 text-white active:bg-red-600',
    success: 'bg-green-500 text-white active:bg-green-600',
    outline:
      'bg-transparent border border-gray-300 text-gray-800 active:bg-gray-100',
    text: 'bg-transparent text-gray-800 active:bg-gray-100',
    'text-danger': 'bg-transparent text-red-600 active:bg-red-100',
  }

  // Add hover styles for devices that support hover
  const variantStyles = hasHover
    ? {
        primary: `${baseVariantStyles.primary} hover:bg-green-600`,
        secondary: `${baseVariantStyles.secondary} hover:bg-gray-300`,
        tertiary: `${baseVariantStyles.tertiary} hover:bg-gray-200`,
        danger: `${baseVariantStyles.danger} hover:bg-red-600`,
        success: `${baseVariantStyles.success} hover:bg-green-600`,
        outline: `${baseVariantStyles.outline} hover:bg-gray-100`,
        text: `${baseVariantStyles.text} hover:bg-gray-100`,
        'text-danger': `${baseVariantStyles['text-danger']} hover:bg-red-100`,
      }
    : baseVariantStyles

  // Width styles
  const widthStyles = fullWidth ? 'w-full' : ''

  return (
    <button
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${widthStyles} ${className}`}
      onClick={onClick}
      {...rest}
    >
      {children}
    </button>
  )
}

export default Button
